import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { ROLE_TALENT_ID } from "../../configs/api";
import { authApi } from "../../services/auth.service";
import { OauthToken } from "../../types/oauth";
import { Manager, Talent, User, UserStatus } from "../../types/users";

export interface AuthState {
  user: Talent | null;
  token: OauthToken | null;
  error: string | null;
}

const initialState: AuthState = {
  user: null,
  token: null,
  error: null,
};

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<Talent | null>) => {
      state.user = action.payload;
    },
    setToken: (state, action: PayloadAction<OauthToken | null>) => {
      state.token = action.payload;
    },
    clearSession: (state) => {
      state.token = null;
      state.user = null;
    },
    resetError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        authApi.endpoints.login.matchFulfilled,
        (state, { payload }) => {
          state.token = {
            accessToken: payload.access_token,
            refreshToken: payload.refresh_token,
            expiresIn: payload.expires_in,
          };
        }
      )
      .addMatcher(
        authApi.endpoints.getUser.matchFulfilled,
        (state, { payload }) => {
          state.user = payload;
        }
      )
      .addMatcher(
        authApi.endpoints.loadUser.matchFulfilled,
        (state, { payload }) => {
          if (payload.role_id == ROLE_TALENT_ID) {
            // check if is_valid_us and status=pending
            if (
              !!payload.talent?.is_valid_us == false &&
              payload.status == UserStatus.PENDING
            ) {
              console.error("Not a valid US and Pending ! Aborted");
              state.user = null;
              state.token = null;
              state.error =
                "Your profile is currently under review and a Workz360 Representative will be reaching out to you with questions.";
            } else {
              // OK
              state.user = payload;
            }
          } else {
            console.error("Not a talent! Aborted");
            state.user = null;
            state.token = null;
            state.error = "Your account doesn't have access to this app.";
          }
        }
      )
      .addMatcher(
        authApi.endpoints.loadUser.matchRejected,
        (state, { error }) => {
          //ConditionError - Aborted due to condition callback returning false
          //happen after reload
          if (error?.name != "ConditionError") {
            // get user failed (token expired etc) ?? logout totally
            state.user = null;
            state.token = null;
          }
        }
      )
      .addMatcher(authApi.endpoints.logout.matchFulfilled, (state) => {
        state.token = null;
        state.user = null;
      });
  },
});

export const { setUser, setToken, clearSession, resetError } =
  authSlice.actions;

export const selectCurrentUser = (state: RootState) => state.auth.user;

export default authSlice.reducer;
